import React, { FC, useEffect, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";

import Quota from "../../components/Quota";
import SaveButton from "../../components/SaveButton";
import SettingSection from "../../components/SettingSection";
import TextField from "../../components/styled/TextField";
import { useDialog } from "../../hooks/use-dialog";
import useQuota from "../../hooks/use-quota";
import { pushSuccess } from "../../slices/notifications";
import { setBannerMessage } from "../../slices/pricing_info";
import { useDispatch, useSelector } from "../../store";
import { useCreateApiAccessUserMutation } from "../../store/pomeriumApi";
import CodeSnippet from "@/components/CodeSnippet";

type AddApiUserProps = Record<string, never>;

const AddApiUser: FC<AddApiUserProps> = () => {
  const [createApiKey, apiKeyResult] = useCreateApiAccessUserMutation();
  const dispatch = useDispatch();
  const dialog = useDialog();
  const [name, setName] = useState<string>("");
  const [token, setToken] = useState<string>("");
  const { organizationId } = useSelector((state) => state.current);
  const { count: apiUserCount, limit: apiUserLimit } = useQuota("apiUsers");

  useEffect(() => {
    if (!dialog.open) {
      setName("");
      setToken("");
    }
  }, [dialog.open]);

  useEffect(() => {
    if (apiKeyResult.isSuccess) {
      setToken(apiKeyResult.data.refreshToken);
      dispatch(pushSuccess("API User Created!"));
    }
  }, [apiKeyResult]);

  const handleOpen = () => {
    if (apiUserCount >= apiUserLimit) {
      dispatch(
        setBannerMessage("To add another API User, upgrade your account."),
      );
    } else {
      dialog.handleOpen();
    }
  };

  const handleCreate = async () => {
    await createApiKey({
      organizationId,
      createApiAccessUserRequest: {
        name: name,
        role: "admin",
      },
    });
  };

  return (
    <Stack>
      <Stack direction="row" spacing={2}>
        <Quota quota="apiUsers" label="API Users" />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleOpen}
        >
          Add API User
        </Button>
      </Stack>
      <Dialog
        open={dialog.open}
        onClose={dialog.handleClose}
        scroll="paper"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          Add API User
          <IconButton
            color="primary"
            onClick={() => dialog.handleClose()}
            size="large"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Container disableGutters>
            <Stack>
              <SettingSection label="Name" docsKey="">
                <TextField
                  error={!name}
                  disabled={!!token}
                  fullWidth
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setName(e.target.value)
                  }
                />
              </SettingSection>
              {token && (
                <Stack>
                  <Typography variant="body1">
                    Copy your API User Token.
                  </Typography>
                  <CodeSnippet
                    code={token}
                    copyInfo="api_user_token"
                    showViewToggle
                  />
                </Stack>
              )}
            </Stack>
          </Container>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dialog.handleClose()}>
            {token ? "OK" : "Cancel"}
          </Button>
          {!token && (
            <SaveButton color="primary" onClick={handleCreate} disabled={!name}>
              Create
            </SaveButton>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AddApiUser;
